import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderReviewsState } from "./orderReviews.state";
import {
  OrderReviewsSummary,
  OrderReview,
  OrderReviewDetail,
} from "./orderReviews.types";
import {
  getOrderReviewsSummary,
  getOrderReviews,
  addOrderReview,
  editOrderReview,
  removeOrderReview,
  getOrderReviewById,
  forceRemoveOrderReview,
} from "./orderReviews.thunk";

const initialState: OrderReviewsState = {
  summary: {
    data: null,
    status: "idle",
    error: null,
  },
  reviews: {
    data: [],
    status: "idle",
    error: null,
  },
  currentReview: {
    data: null,
    status: "idle",
    error: null,
  },
  createStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  error: null,
};

export const orderReviewsSlice = createSlice({
  name: "orderReviews",
  initialState,
  reducers: {
    resetOrderReviews: (state) => {
      state.summary = initialState.summary;
      state.reviews = initialState.reviews;
      state.currentReview = initialState.currentReview;
      state.createStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
      state.error = null;
    },
    resetOrderReviewsList: (state) => {
      state.reviews.data = [];
      state.reviews.status = "idle";
      state.reviews.error = null;
    },
    resetCurrentReview: (state) => {
      state.currentReview = initialState.currentReview;
    },
    resetCreateStatus: (state) => {
      state.createStatus = "idle";
      state.error = null;
    },
    resetUpdateStatus: (state) => {
      state.updateStatus = "idle";
      state.error = null;
    },
    resetDeleteStatus: (state) => {
      state.deleteStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderReviewsSummary.pending, (state) => {
        state.summary.status = "loading";
        state.summary.error = null;
      })
      .addCase(
        getOrderReviewsSummary.fulfilled,
        (state, action: PayloadAction<OrderReviewsSummary>) => {
          state.summary.status = "success";
          state.summary.data = action.payload;
          state.summary.error = null;
        },
      )
      .addCase(
        getOrderReviewsSummary.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.summary.status = "error";
          state.summary.error =
            action.payload ?? "Değerlendirme özeti yüklenemedi.";
        },
      )
      .addCase(getOrderReviews.pending, (state) => {
        state.reviews.status = "loading";
        state.reviews.error = null;
      })
      .addCase(
        getOrderReviews.fulfilled,
        (state, action: PayloadAction<OrderReview[]>) => {
          state.reviews.status = "success";
          state.reviews.data = action.payload;
          state.reviews.error = null;
        },
      )
      .addCase(
        getOrderReviews.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.reviews.status = "error";
          state.reviews.error =
            action.payload ?? "Değerlendirmeler yüklenemedi.";
        },
      )
      .addCase(addOrderReview.pending, (state) => {
        state.createStatus = "loading";
        state.error = null;
      })
      .addCase(
        addOrderReview.fulfilled,
        (state, action: PayloadAction<OrderReviewDetail>) => {
          state.createStatus = "success";
          state.error = null;
          if (state.reviews.data) {
            state.reviews.data.unshift({
              ...action.payload,
              seller_rating: action.payload.rating.seller,
              cargo_rating: action.payload.rating.cargo,
            });
          }
        },
      )
      .addCase(
        addOrderReview.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.createStatus = "error";
          state.error = action.payload ?? "Değerlendirme kaydedilemedi.";
        },
      )
      .addCase(editOrderReview.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(
        editOrderReview.fulfilled,
        (state, action: PayloadAction<OrderReviewDetail>) => {
          state.updateStatus = "success";
          state.error = null;
          if (state.reviews.data) {
            const index = state.reviews.data.findIndex(
              (review) =>
                review.order_review_id === action.payload.order_review_id,
            );
            if (index !== -1) {
              state.reviews.data[index] = {
                ...action.payload,
                seller_rating: action.payload.rating.seller,
                cargo_rating: action.payload.rating.cargo,
              };
            }
          }
        },
      )
      .addCase(
        editOrderReview.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.updateStatus = "error";
          state.error = action.payload ?? "Değerlendirme güncellenemedi.";
        },
      )
      .addCase(removeOrderReview.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(
        removeOrderReview.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.deleteStatus = "success";
          state.error = null;
          if (state.reviews.data) {
            state.reviews.data = state.reviews.data.filter(
              (review) => review.order_review_id !== action.payload,
            );
          }
        },
      )
      .addCase(
        removeOrderReview.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.deleteStatus = "error";
          state.error = action.payload ?? "Değerlendirme silinemedi.";
        },
      )
      .addCase(getOrderReviewById.pending, (state) => {
        state.currentReview.status = "loading";
        state.currentReview.error = null;
      })
      .addCase(
        getOrderReviewById.fulfilled,
        (state, action: PayloadAction<OrderReviewDetail>) => {
          state.currentReview.status = "success";
          state.currentReview.data = action.payload;
        },
      )
      .addCase(
        getOrderReviewById.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.currentReview.status = "error";
          state.currentReview.error =
            action.payload ?? "Değerlendirme yüklenemedi.";
        },
      )
      .addCase(forceRemoveOrderReview.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(
        forceRemoveOrderReview.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.deleteStatus = "success";
          state.error = null;
          if (state.reviews.data) {
            state.reviews.data = state.reviews.data.filter(
              (review) => review.order_review_id !== action.payload,
            );
          }
        },
      )
      .addCase(
        forceRemoveOrderReview.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.deleteStatus = "error";
          state.error =
            action.payload ?? "Değerlendirme kalıcı olarak silinemedi.";
        },
      );
  },
});

export const {
  resetOrderReviews,
  resetOrderReviewsList,
  resetCurrentReview,
  resetCreateStatus,
  resetUpdateStatus,
  resetDeleteStatus,
} = orderReviewsSlice.actions;

export default orderReviewsSlice.reducer;
